/**
 * INSPECT RELEASE FIELDS (Admin Tool)
 *
 * Diagnostic-only. Shows the current signed-in user's items where any
 * of the release-info fields are populated (eventSource, eventName,
 * eventTheme, boxName, boxTheme, collectionName, releaseType). Purpose:
 * settle the eventSource-vs-eventName semantic question by looking at
 * REAL data instead of guessing.
 *
 * Read-only. No writes. Displays as a table + JSON dump so the operator
 * can screenshot or copy-paste for further analysis.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection as fsCollection,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, Search, Copy, Download } from 'lucide-react';

interface Row {
  id: string;
  name: string;
  brand: string;
  itemType: string;
  releaseType: string;
  eventSource: string;
  eventName: string;
  eventTheme: string;
  boxName: string;
  boxTheme: string;
  collectionName: string;
}

const RELEVANT_FIELDS: (keyof Row)[] = [
  'releaseType',
  'eventSource',
  'eventName',
  'eventTheme',
  'boxName',
  'boxTheme',
  'collectionName',
];

export default function InspectReleaseFields() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [error404, setError404] = useState<string | null>(null);
  /** Buckets for the summary panel — same evidence four different
   *  ways so the semantic question can be answered by inspection. */
  const [stats, setStats] = useState<{
    total: number;
    onlyEventSource: number;
    onlyEventName: number;
    bothSame: number;
    bothDifferent: number;
    neither: number;
  } | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const load = async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    setError404(null);
    try {
      const snap = await getDocs(
        fsCollection(db, 'users', currentUser, 'items')
      );
      const collected: Row[] = [];
      let total = 0;
      let onlyEventSource = 0;
      let onlyEventName = 0;
      let bothSame = 0;
      let bothDifferent = 0;
      let neither = 0;
      for (const d of snap.docs) {
        const data = d.data() as Record<string, unknown>;
        if (data.isDeleted === true) continue;
        total++;
        const eventSource =
          typeof data.eventSource === 'string' ? data.eventSource.trim() : '';
        const eventName =
          typeof data.eventName === 'string' ? data.eventName.trim() : '';
        if (!eventSource && !eventName) neither++;
        else if (eventSource && !eventName) onlyEventSource++;
        else if (!eventSource && eventName) onlyEventName++;
        else if (eventSource === eventName) bothSame++;
        else bothDifferent++;

        // Show rows where ANY release-info field is populated so the
        // operator can eyeball what fields go with what release types.
        const releaseType =
          typeof data.releaseType === 'string' ? data.releaseType : '';
        const eventTheme =
          typeof data.eventTheme === 'string' ? data.eventTheme.trim() : '';
        const boxName =
          typeof data.boxName === 'string' ? data.boxName.trim() : '';
        const boxTheme =
          typeof data.boxTheme === 'string' ? data.boxTheme.trim() : '';
        const collectionName =
          typeof data.collectionName === 'string' ? data.collectionName.trim() : '';
        if (
          eventSource ||
          eventName ||
          eventTheme ||
          boxName ||
          boxTheme ||
          collectionName ||
          releaseType === 'event' ||
          releaseType === 'box'
        ) {
          collected.push({
            id: d.id,
            name: typeof data.name === 'string' ? data.name : '(unnamed)',
            brand: typeof data.brand === 'string' ? data.brand : '',
            itemType: typeof data.itemType === 'string' ? data.itemType : 'polish',
            releaseType,
            eventSource,
            eventName,
            eventTheme,
            boxName,
            boxTheme,
            collectionName,
          });
        }
      }
      collected.sort((a, b) =>
        a.brand.localeCompare(b.brand) || a.name.localeCompare(b.name)
      );
      setRows(collected);
      setStats({ total, onlyEventSource, onlyEventName, bothSame, bothDifferent, neither });
      if (collected.length === 0) {
        setError404(
          `Found ${total} items but none have any of the release-info fields set.`
        );
      }
    } catch (err) {
      setError(`Failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">Checking access…</div>
    );
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Admin only.
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2 inline-flex items-center gap-2">
        <Search className="w-6 h-6" /> Inspect release fields
      </h1>
      <p className="text-glass-secondary text-sm mb-6">
        Read-only diagnostic. Reads only YOUR user items and displays how
        the release fields (<code>eventSource</code>, <code>eventName</code>,
        <code>boxName</code>, <code>collectionName</code>, etc.) are populated
        so we can decide whether eventSource and eventName represent the
        same or different concepts.
      </p>

      <button
        onClick={load}
        disabled={loading}
        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 mb-4"
      >
        {loading ? 'Loading…' : 'Load my items'}
      </button>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-300">
          {error}
        </div>
      )}
      {error404 && (
        <div className="mb-4 p-3 bg-slate-800/40 border border-slate-600 rounded text-glass-secondary">
          {error404}
        </div>
      )}

      {stats && (
        <div className="bg-slate-800/40 border border-slate-600 rounded p-4 mb-6 grid grid-cols-3 gap-3 text-sm text-glass-secondary">
          <div>
            <div className="text-glass-muted text-xs">Total items scanned</div>
            <div className="text-glass text-lg font-semibold">{stats.total}</div>
          </div>
          <div>
            <div className="text-glass-muted text-xs">Only eventSource set</div>
            <div className="text-glass text-lg font-semibold">{stats.onlyEventSource}</div>
          </div>
          <div>
            <div className="text-glass-muted text-xs">Only eventName set</div>
            <div className="text-glass text-lg font-semibold">{stats.onlyEventName}</div>
          </div>
          <div>
            <div className="text-glass-muted text-xs">Both, same value</div>
            <div className="text-glass text-lg font-semibold">{stats.bothSame}</div>
          </div>
          <div>
            <div className="text-glass-muted text-xs">Both, DIFFERENT values</div>
            <div className="text-glass text-lg font-semibold text-amber-300">
              {stats.bothDifferent}
            </div>
          </div>
          <div>
            <div className="text-glass-muted text-xs">Neither field set</div>
            <div className="text-glass text-lg font-semibold">{stats.neither}</div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-glass mb-2">
            Items with release-info populated ({rows.length})
          </h2>
          <div className="overflow-x-auto border border-slate-600 rounded">
            <table className="w-full text-xs text-glass-secondary">
              <thead className="bg-slate-800/60 text-glass-muted uppercase">
                <tr>
                  <th className="p-2 text-left">Name / Brand</th>
                  <th className="p-2 text-left">itemType</th>
                  <th className="p-2 text-left">releaseType</th>
                  <th className="p-2 text-left">eventSource</th>
                  <th className="p-2 text-left">eventName</th>
                  <th className="p-2 text-left">eventTheme</th>
                  <th className="p-2 text-left">boxName</th>
                  <th className="p-2 text-left">boxTheme</th>
                  <th className="p-2 text-left">collectionName</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-slate-700 hover:bg-slate-800/40"
                  >
                    <td className="p-2 text-glass whitespace-nowrap">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-glass-muted">{r.brand}</div>
                    </td>
                    <td className="p-2">{r.itemType}</td>
                    <td className="p-2">{r.releaseType}</td>
                    <td className="p-2">{r.eventSource || <span className="text-glass-muted">—</span>}</td>
                    <td className="p-2">{r.eventName || <span className="text-glass-muted">—</span>}</td>
                    <td className="p-2">{r.eventTheme || <span className="text-glass-muted">—</span>}</td>
                    <td className="p-2">{r.boxName || <span className="text-glass-muted">—</span>}</td>
                    <td className="p-2">{r.boxTheme || <span className="text-glass-muted">—</span>}</td>
                    <td className="p-2">{r.collectionName || <span className="text-glass-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Copy + Download buttons — reliable way to get the data out
              without wrestling with browser text-selection on a big
              pre. */}
          {(() => {
            const jsonPayload = JSON.stringify(
              rows.map((r) => {
                const out: Record<string, string> = {};
                for (const k of RELEVANT_FIELDS) {
                  if (r[k]) out[k] = r[k] as string;
                }
                return { name: r.name, brand: r.brand, itemType: r.itemType, ...out };
              }),
              null,
              2
            );
            const doCopy = async () => {
              try {
                await navigator.clipboard.writeText(jsonPayload);
                alert('Copied to clipboard.');
              } catch (err) {
                alert(`Copy failed: ${(err as Error).message}. Try Download instead.`);
              }
            };
            const doDownload = () => {
              const blob = new Blob([jsonPayload], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'release-fields-export.json';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            };
            return (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={doCopy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600"
                >
                  <Copy className="w-4 h-4" /> Copy JSON
                </button>
                <button
                  onClick={doDownload}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600"
                >
                  <Download className="w-4 h-4" /> Download JSON
                </button>
              </div>
            );
          })()}

          <details className="mt-4">
            <summary className="cursor-pointer text-glass-secondary text-sm">
              Show raw JSON (also selectable manually)
            </summary>
            <pre className="mt-2 p-3 bg-slate-900 border border-slate-700 rounded text-xs text-glass-muted whitespace-pre-wrap overflow-x-auto select-text">
              {JSON.stringify(
                rows.map((r) => {
                  const out: Record<string, string> = {};
                  for (const k of RELEVANT_FIELDS) {
                    if (r[k]) out[k] = r[k] as string;
                  }
                  return { name: r.name, brand: r.brand, itemType: r.itemType, ...out };
                }),
                null,
                2
              )}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}
